import { Controller } from '@hotwired/stimulus';

export default class CancellationFeeController extends Controller {
  static targets = ['date', 'feeInput', 'automaticCheckbox', 'daysOutput', 'loading', 'error'];

  static values = {
    url: String,
  };

  connect() {
    console.log("initializing cancellation fee");
    this.abortController = null;
    this.updateFeeInputState();
  }

  disconnect() {
    this.abortOngoingRequest();
  }

  toggleAutomatic() {
    this.updateFeeInputState();

    if (this.isAutomatic()) {
      this.fetchFee();
    } else {
      this.abortOngoingRequest();
      this.hideLoading();
      this.clearError();
    }
  }

  dateChanged() {
    if (!this.isAutomatic()) return;
    if (!this.dateTarget.value) return;

    this.fetchFee();
  }

  isAutomatic() {
    return this.automaticCheckboxTarget.checked;
  }

  updateFeeInputState() {
    this.feeInputTarget.readOnly = this.isAutomatic();
    this.feeInputTarget.classList.toggle('opacity-60', this.isAutomatic());
  }

  abortOngoingRequest() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  async fetchFee() {
    this.abortOngoingRequest();
    this.abortController = new AbortController();

    this.showLoading();
    this.clearError();

    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set('canceled_at', this.dateTarget.value);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();

      if (!this.isAutomatic()) return;

      if (data.fee !== undefined) {
        this.feeInputTarget.value = data.fee;
      }

      if (this.hasDaysOutputTarget && data.distance_in_days !== undefined) {
        this.daysOutputTarget.textContent = data.distance_in_days;
      }
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error('Cancellation fee recalculation failed:', error);
      this.showError('The cancellation fee could not be recalculated.');
    } finally {
      this.hideLoading();
      this.abortController = null;
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden');
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden');
    }
  }

  showError(message) {
    if (!this.hasErrorTarget) return;

    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove('hidden');
  }

  clearError() {
    if (!this.hasErrorTarget) return;

    this.errorTarget.textContent = '';
    this.errorTarget.classList.add('hidden');
  }
}
